import { hasPermission, type AuthUser, type Permission } from '@riba/shared';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  setSession: (token: string, user: AuthUser) => void;
  clear: () => void;
}

/**
 * Staff session. The token is a 12h MoySklad-backed JWT with no refresh flow
 * (same model as the WMS PWA), so it is persisted to localStorage — otherwise
 * every page reload would force a re-login.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      setSession: (token, user) => set({ token, user }),
      clear: () => set({ token: null, user: null }),
    }),
    { name: 'riba-admin-auth' },
  ),
);

/**
 * Permission check against the shared RBAC map — the same map the backend
 * enforces with, so the UI never offers an action the API would refuse.
 */
export function useCan(permission: Permission): boolean {
  const user = useAuthStore((s) => s.user);
  return hasPermission(user, permission);
}
